<template>
  <section class="flex flex-col gap-4 rounded-lg border p-4">
    <header class="flex items-center justify-between gap-3">
      <h3 class="text-base font-semibold text-foreground">Checklists</h3>
      <Button v-if="!readonly" type="button" size="sm" :loading="creatingChecklist" :disabled="creatingChecklist" @click="createChecklist"> Add Checklist </Button>
    </header>

    <p v-if="createErrorMessage" class="text-sm text-destructive">
      {{ createErrorMessage }}
    </p>

    <p v-if="loadErrorMessage" class="text-sm text-destructive">
      {{ loadErrorMessage }}
    </p>

    <div v-if="checklistsStore.isLoading && visibleChecklists.length === 0" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">Loading checklists...</div>

    <p v-else-if="!checklistsStore.isLoading && visibleChecklists.length === 0" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
      No checklists are linked to this record yet.
    </p>

    <div v-else class="flex flex-col gap-4">
      <Checklist
        v-for="checklist in visibleChecklists"
        :key="checklist.id"
        :checklist="checklist"
        :is-not-executable="isNotExecutable"
        :readonly="readonly"
        :initially-minimized="!isChecklistExpanded(checklist.id)"
        :focus-quick-add-signal="quickAddFocusSignals[checklist.id] ?? 0"
        :show-manage-actions="!readonly"
        :is-deleting="deletingChecklistIds.includes(checklist.id)"
        @edit="openEditModal"
        @delete="deleteChecklist"
      />
    </div>
  </section>

  <ChecklistEditModal ref="checklistEditModal" :checklistable-id="props.checklistableId" :checklistable-type="props.checklistableType" />
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { Button } from '@/components/ui/button/index.js'
import Checklist from './Checklist.vue'
import ChecklistEditModal from './ChecklistEditModal.vue'
import { useChecklistsStore } from '../../stores/checklistsStore.js'

const emit = defineEmits(['count-changed'])

const props = defineProps({
  checklistableId: {
    type: [Number, String],
    required: true,
  },
  checklistableType: {
    type: String,
    required: true,
  },
  isNotExecutable: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
})

const checklistsStore = useChecklistsStore(`checklists_card_${String(props.checklistableType).replace(/[^A-Za-z0-9]/g, '_')}_${props.checklistableId}`)
const createErrorMessage = ref(null)
const creatingChecklist = ref(false)
const deletingChecklistIds = ref([])
const expandedChecklistIds = ref([])
const quickAddFocusSignals = ref({})
const checklistEditModal = ref(null)
const normalizedChecklistableId = computed(() => Number(props.checklistableId))
const normalizedChecklistableType = computed(() => String(props.checklistableType).trim())

const loadErrorMessage = computed(() => {
  if (!checklistsStore.error) {
    return null
  }

  return checklistsStore.error.response?.data?.message ?? checklistsStore.error.message ?? 'Unable to load checklists.'
})

const visibleChecklists = computed(() => {
  if (normalizedChecklistableId.value < 1 || normalizedChecklistableType.value.length === 0) {
    return checklistsStore.items
  }

  return checklistsStore.items.filter((checklist) => {
    const checklistableId = Number(checklist.checklistable_id ?? checklist.pivot?.checklistable_id ?? 0)
    const checklistableType = String(checklist.checklistable_type ?? checklist.pivot?.checklistable_type ?? '').trim()

    if (checklistableId < 1 || checklistableType.length === 0) {
      return true
    }

    return checklistableId === normalizedChecklistableId.value && checklistableType === normalizedChecklistableType.value
  })
})

const fetchChecklists = async () => {
  await checklistsStore.fetchItems({
    clear: true,
    not_paginated: true,
    checklistable_id: Number(props.checklistableId),
    checklistable_type: props.checklistableType,
    order_by: 'started_at',
    order_direction: 'desc',
  })
  emit('count-changed', visibleChecklists.value.length)
}

const isChecklistExpanded = (checklistId) => {
  return expandedChecklistIds.value.includes(Number(checklistId))
}

const expandChecklist = (checklistId) => {
  const normalizedChecklistId = Number(checklistId)

  if (normalizedChecklistId < 1 || expandedChecklistIds.value.includes(normalizedChecklistId)) {
    return
  }

  expandedChecklistIds.value.push(normalizedChecklistId)
}

const triggerChecklistQuickAddFocus = (checklistId) => {
  const normalizedChecklistId = Number(checklistId)

  if (normalizedChecklistId < 1) {
    return
  }

  quickAddFocusSignals.value = {
    ...quickAddFocusSignals.value,
    [normalizedChecklistId]: (quickAddFocusSignals.value[normalizedChecklistId] ?? 0) + 1,
  }
}

const createChecklist = async (options = {}) => {
  if (props.readonly || creatingChecklist.value) {
    return null
  }

  createErrorMessage.value = null
  creatingChecklist.value = true

  try {
    const checklist = await checklistsStore.itemCreate({
      name: 'Checklist',
      checklistable_id: Number(props.checklistableId),
      checklistable_type: props.checklistableType,
    })
    await fetchChecklists()
    expandChecklist(checklist?.id)
    triggerChecklistQuickAddFocus(checklist?.id)
    return checklist
  } catch (error) {
    createErrorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to create checklist.'
    if (options.rethrow === true) {
      throw error
    }

    return null
  } finally {
    creatingChecklist.value = false
  }
}

const createChecklistFromFeatureMenu = async () => {
  return createChecklist({ rethrow: true })
}

const openEditModal = (checklist) => {
  if (props.readonly) {
    return
  }

  checklistEditModal.value?.openModal?.(checklist)
}

const deleteChecklist = async (checklist) => {
  if (props.readonly) {
    return
  }

  if (!window.confirm('Delete this checklist?')) {
    return
  }

  deletingChecklistIds.value.push(checklist.id)

  try {
    await checklistsStore.itemDelete(checklist.id)
  } finally {
    deletingChecklistIds.value = deletingChecklistIds.value.filter((id) => id !== checklist.id)
  }
}

watch(
  () => [props.checklistableId, props.checklistableType],
  async () => {
    checklistEditModal.value?.closeModal?.()
    expandedChecklistIds.value = []
    quickAddFocusSignals.value = {}
    await fetchChecklists()
  },
)

onMounted(async () => {
  await fetchChecklists()
})

watch(
  () => visibleChecklists.value.length,
  (count) => {
    emit('count-changed', count)
  },
)

defineExpose({
  createChecklistFromFeatureMenu,
  fetchChecklists,
})
</script>
